import React, { useEffect, useRef, useState } from "react";
import {
  getFeaturedMedia,
  updateFeaturedMedia,
  getSectionImages,
  addSectionImage,
  deleteSectionImage,
} from "../../services/featuredMediaService";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";

const BASE = "https://opiniongames.net/";
const toastCfg = { autoClose: 1500 };

/* ─── Styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerWrap: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 },
  pageTitle: { margin: 0, fontWeight: 800, fontSize: "1.5rem", color: "#0f172a" },
  pageSub: { margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", alignItems: "start", marginBottom: 24 },
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  cardHeader: { padding: "14px 20px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc", fontWeight: 800, color: "#0f172a", fontSize: "0.95rem", display: "flex", alignItems: "center", gap: 8 },
  cardBody: { padding: "20px" },
  label: { display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: { width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit", marginBottom: 14, boxSizing: "border-box" },
  inputFocus: { borderColor: "#1da144" },
  saveBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "11px 24px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer", width: "100%", marginTop: 4 },
  saveBtnDisabled: { opacity: 0.65, cursor: "not-allowed" },
  detailRow: { display: "flex", flexDirection: "column", paddingBottom: 14, marginBottom: 14, borderBottom: "1px solid #f1f5f9" },
  detailRowNoBorder: { display: "flex", flexDirection: "column" },
  detailLabel: { fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 },
  detailValue: { fontSize: "0.88rem", fontWeight: 600, color: "#1e293b", wordBreak: "break-all" },
  badge: { display: "inline-flex", alignItems: "center", gap: 4, background: "#dcfce7", color: "#16a34a", padding: "3px 10px", borderRadius: 100, fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase", border: "1px solid #bbf7d0" },
  badgeInactive: { display: "inline-flex", alignItems: "center", gap: 4, background: "#fee2e2", color: "#dc2626", padding: "3px 10px", borderRadius: 100, fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase", border: "1px solid #fecaca" },
  imgPreviewBox: { marginTop: 8, background: "#f8fafc", padding: 10, borderRadius: 12, border: "1px dashed #cbd5e1" },
  emptyImg: { padding: "36px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem", fontWeight: 600 },
  /* gallery section */
  galleryCard: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  uploadRow: { display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 12, alignItems: "flex-end", marginBottom: 20 },
  addBtn: { display: "flex", alignItems: "center", gap: 6, padding: "10px 20px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer", whiteSpace: "nowrap" },
  imagesGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 16 },
  imgCard: { borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0", background: "#f8fafc", position: "relative" },
  imgCardImg: { width: "100%", height: 130, objectFit: "cover", display: "block" },
  deleteOverlay: { position: "absolute", top: 6, right: 6 },
  deleteBtn: { background: "#ef4444", border: "none", borderRadius: 7, padding: "4px 7px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  imgMeta: { padding: "8px 10px", fontSize: "0.72rem", color: "#64748b", fontWeight: 600 },
  emptyGallery: { padding: "40px 20px", textAlign: "center", color: "#94a3b8", gridColumn: "1/-1" },
};

export const FeaturedMediaListLayer = () => {
  /* ── Main media state ── */
  const [media, setMedia] = useState(null);
  const [form, setForm] = useState({ image_url: "", youtube_link: "", start_time: "30", status: "active" });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  /* ── Section images state ── */
  const [sections, setSections] = useState([]);
  const [secLoading, setSecLoading] = useState(false);
  const [secForm, setSecForm] = useState({ section_id: "" });
  const [secFile, setSecFile] = useState(null);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const secFileRef = useRef();

  useEffect(() => { loadMedia(); loadSections(); }, []);

  /* ── Loaders ── */
  const loadMedia = () => {
    getFeaturedMedia()
      .then((res) => {
        if (res?.status) {
          const d = res.data ?? res;
          setMedia(d);
          setForm({
            image_url: d.image_url || "",
            youtube_link: d.youtube_link || "",
            start_time: d.start_time ?? "30",
            status: d.status || "active",
          });
        }
      })
      .catch(() => toast.error("Failed to load media", toastCfg));
  };

  const loadSections = () => {
    setSecLoading(true);
    getSectionImages()
      .then((res) => {
        if (res?.status) setSections(res.data ?? []);
      })
      .catch(() => {})
      .finally(() => setSecLoading(false));
  };

  /* ── Main media save ── */
  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    const data = new FormData();
    data.append("image_url", form.image_url);
    data.append("youtube_link", form.youtube_link);
    data.append("start_time", form.start_time);
    data.append("status", form.status);
    if (imageFile) data.append("image_file", imageFile);

    updateFeaturedMedia(data)
      .then((res) => {
        if (res?.status) {
          toast.success("Media updated successfully", toastCfg);
          setImageFile(null);
          loadMedia();
        } else {
          toast.error(res?.message || "Update failed", toastCfg);
        }
      })
      .catch(() => toast.error("Update failed", toastCfg))
      .finally(() => setSaving(false));
  };

  /* ── Section image add ── */
  const handleAddSection = (e) => {
    e.preventDefault();
    if (!secFile) return toast.warn("Please select an image", toastCfg);
    setAdding(true);
    const data = new FormData();
    if (secForm.section_id) data.append("section_id", secForm.section_id);
    data.append("image_file", secFile);

    addSectionImage(data)
      .then((res) => {
        if (res?.status) {
          toast.success("Image added", toastCfg);
          setSecForm({ section_id: "" });
          setSecFile(null);
          if (secFileRef.current) secFileRef.current.value = "";
          loadSections();
        } else {
          toast.error(res?.message || "Failed to add image", toastCfg);
        }
      })
      .catch(() => toast.error("Failed to add image", toastCfg))
      .finally(() => setAdding(false));
  };

  /* ── Section image delete ── */
  const handleDelete = (id) => {
    setDeletingId(id);
    deleteSectionImage(id)
      .then((res) => {
        if (res?.status) {
          toast.success("Image deleted", toastCfg);
          setSections((prev) => prev.filter((img) => img.id !== id));
        } else {
          toast.error(res?.message || "Delete failed", toastCfg);
        }
      })
      .catch(() => toast.error("Delete failed", toastCfg))
      .finally(() => setDeletingId(null));
  };

  /* ── Loading screen ── */
  if (!media) {
    return (
      <div style={{ ...s.root, display: "flex", justifyContent: "center", alignItems: "center", gap: 10, color: "#64748b" }}>
        <span className="spinner-border spinner-border-sm" style={{ color: "#1da144" }} />
        Loading media settings…
      </div>
    );
  }

  return (
    <div style={s.root}>
      {/* ── Header ── */}
      <div style={s.headerWrap}>
        <div>
          <h2 style={s.pageTitle}>Featured Media</h2>
          <p style={s.pageSub}>Manage promotional banner, YouTube embed, and section gallery images</p>
        </div>
      </div>

      {/* ── Row 1: Editor + Preview ── */}
      <div style={s.grid2}>
        {/* Editor Card */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <Icon icon="solar:pen-bold-duotone" style={{ color: "#3b82f6", fontSize: 18 }} />
            Banner Settings
          </div>
          <div style={s.cardBody}>
            <form onSubmit={handleSave}>
              <label style={s.label}>Redirect URL (optional)</label>
              <input
                style={s.input}
                type="text"
                placeholder="https://site.com/banner.jpg"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              />

              <label style={s.label}>YouTube Link</label>
              <input
                style={s.input}
                type="text"
                placeholder="https://www.youtube.com/watch?v=..."
                value={form.youtube_link}
                onChange={(e) => setForm({ ...form, youtube_link: e.target.value })}
              />

              <label style={s.label}>Start Time (seconds)</label>
              <input
                style={s.input}
                type="number"
                min="0"
                placeholder="30"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              />

              <label style={s.label}>Visibility</label>
              <select
                style={s.input}
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">Active (Visible)</option>
                <option value="inactive">Inactive (Hidden)</option>
              </select>

              <label style={s.label}>Cover Image</label>
              <input
                style={s.input}
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0] || null)}
              />

              <button type="submit" style={{ ...s.saveBtn, ...(saving ? s.saveBtnDisabled : {}) }} disabled={saving}>
                {saving ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <Icon icon="solar:server-square-bold" style={{ fontSize: 16 }} />
                    Publish Changes
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Live Preview Card */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <Icon icon="solar:eye-bold-duotone" style={{ color: "#1da144", fontSize: 18 }} />
            Live Preview
          </div>
          <div style={s.cardBody}>
            <div style={s.detailRow}>
              <div style={s.detailLabel}>Status</div>
              <div>
                <span style={media.status === "active" ? s.badge : s.badgeInactive}>
                  <Icon icon={media.status === "active" ? "solar:check-circle-bold" : "solar:close-circle-bold"} style={{ fontSize: 11 }} />
                  {media.status}
                </span>
              </div>
            </div>

            <div style={s.detailRow}>
              <div style={s.detailLabel}>YouTube Link</div>
              <div style={s.detailValue}>
                {media.youtube_link ? (
                  <a href={media.youtube_link} target="_blank" rel="noreferrer" style={{ color: "#2563eb", textDecoration: "none" }}>
                    {media.youtube_link}
                  </a>
                ) : "—"}
              </div>
            </div>

            <div style={s.detailRow}>
              <div style={s.detailLabel}>Start Time</div>
              <div style={s.detailValue}>{media.start_time != null ? `${media.start_time}s` : "—"}</div>
            </div>

            {media.image_url && (
              <div style={s.detailRow}>
                <div style={s.detailLabel}>Redirect URL</div>
                <div style={s.detailValue}>{media.image_url}</div>
              </div>
            )}

            <div style={s.detailRowNoBorder}>
              <div style={s.detailLabel}>Current Banner Image</div>
              <div style={s.imgPreviewBox}>
                {media.image_path ? (
                  <img
                    src={`${BASE}${media.image_path}`}
                    alt="featured banner"
                    style={{ width: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 8 }}
                  />
                ) : (
                  <div style={s.emptyImg}>
                    <Icon icon="solar:gallery-bold-duotone" style={{ fontSize: 32, display: "block", margin: "0 auto 8px", color: "#cbd5e1" }} />
                    No cover image uploaded
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 2: Section Images Gallery ── */}
      <div style={s.galleryCard}>
        <div style={s.cardHeader}>
          <Icon icon="solar:gallery-wide-bold-duotone" style={{ color: "#f59e0b", fontSize: 18 }} />
          Section Images Gallery
        </div>
        <div style={s.cardBody}>
          {/* Upload strip */}
          <form onSubmit={handleAddSection} style={s.uploadRow}>
            <div>
              <label style={s.label}>Section ID (optional)</label>
              <input
                style={{ ...s.input, marginBottom: 0 }}
                type="number"
                min="1"
                placeholder="e.g. 3"
                value={secForm.section_id}
                onChange={(e) => setSecForm({ section_id: e.target.value })}
              />
            </div>
            <div>
              <label style={s.label}>Image File</label>
              <input
                ref={secFileRef}
                style={{ ...s.input, marginBottom: 0 }}
                type="file"
                accept="image/*"
                onChange={(e) => setSecFile(e.target.files[0] || null)}
              />
            </div>
            <button
              type="submit"
              style={{ ...s.addBtn, ...(adding ? s.saveBtnDisabled : {}) }}
              disabled={adding}
            >
              {adding ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <>
                  <Icon icon="solar:add-circle-bold" style={{ fontSize: 16 }} />
                  Add Image
                </>
              )}
            </button>
          </form>

          {/* Gallery grid */}
          {secLoading ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#94a3b8" }}>
              <span className="spinner-border spinner-border-sm" style={{ color: "#1da144", marginRight: 8 }} />
              Loading images…
            </div>
          ) : (
            <div style={s.imagesGrid}>
              {sections.length === 0 ? (
                <div style={s.emptyGallery}>
                  <Icon icon="solar:gallery-add-bold-duotone" style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 10px" }} />
                  No section images uploaded yet
                </div>
              ) : (
                sections.map((img) => (
                  <div key={img.id} style={s.imgCard}>
                    <img
                      src={`${BASE}${img.image_path ?? img.image}`}
                      alt={`section-${img.id}`}
                      style={s.imgCardImg}
                      onError={(e) => { e.target.src = "https://placehold.co/160x130?text=No+Image"; }}
                    />
                    <div style={s.deleteOverlay}>
                      <button
                        style={{ ...s.deleteBtn, ...(deletingId === img.id ? { opacity: 0.65 } : {}) }}
                        onClick={() => handleDelete(img.id)}
                        disabled={deletingId === img.id}
                        title="Delete image"
                      >
                        {deletingId === img.id
                          ? <span className="spinner-border spinner-border-sm" style={{ color: "#fff", width: 12, height: 12 }} />
                          : <Icon icon="solar:trash-bin-trash-bold" style={{ color: "#fff", fontSize: 14 }} />}
                      </button>
                    </div>
                    {img.section_id && (
                      <div style={s.imgMeta}>Section #{img.section_id}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
